import axios from 'axios';
import { encryptFilters, decryptFilters } from './encryptionService';

const API_BASE_URL = process.env.REACT_APP_SAVED_FILTERS_API_URL;

// Get user data
const getAuthData = () => {
  try {
    const allKeys = Object.keys(localStorage);

    const lastAuthUserKey = allKeys.find(
      k => k.includes('CognitoIdentityServiceProvider') && k.includes('LastAuthUser'),
    );
    if (!lastAuthUserKey) return { token: null, userId: null };

    const parts = lastAuthUserKey.split('.');
    const clientIdIndex = parts.findIndex(p => p === 'CognitoIdentityServiceProvider') + 1;
    const clientId = parts[clientIdIndex];
    const userSub = localStorage.getItem(lastAuthUserKey);

    if (!clientId || !userSub) return { token: null, userId: null };

    const possibleBaseKeys = [
      `CognitoIdentityServiceProvider.${clientId}.${userSub}`,
      `CognitoIdentityServiceProvider.${clientId}.LastAuthUser.${userSub}`,
    ];

    let idToken = null as string | null;
    let accessToken = null as string | null;

    for (const keyBase of possibleBaseKeys) {
      const id = localStorage.getItem(`${keyBase}.idToken`);
      const acc = localStorage.getItem(`${keyBase}.accessToken`);
      if (id || acc) {
        idToken = id;
        accessToken = acc;
        break;
      }
    }

    if (!idToken && !accessToken) {
      console.warn('No idToken or accessToken found for clientId:', clientId);
      return { token: null, userId: null };
    }

    const token = idToken || accessToken;
    const payload = parseJwt(token);
    const userId = payload?.sub || userSub || null;

    return { token, userId };
  } catch (e) {
    console.error('Error extracting Cognito tokens:', e);
    return { token: null, userId: null };
  }
};

// Parse user token
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const getAuthToken = () => {
  return getAuthData().token;
};

// Create api URL call
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

/**
 * Save filters
 * @param {string} name
 * @param {Array} filters
 * @param {boolean} isEvidence
 * @param {string} searchQuery
 * @param {string} type
 */
export const saveFilter = async (name, filters, isEvidence = false, searchQuery = null, type = 'filter') => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const encryptedFilters = filters ? encryptFilters(filters, userId) : [];

    const payload = {
      name,
      filters: encryptedFilters,
      isEvidence,
      searchQuery,
      type,
    };

    if (searchQuery) {
      payload.searchQuery = searchQuery;
    }

    const response = await apiClient.post('/filters', payload);

    if (response.data.filter && response.data.filter.filters) {
      response.data.filter.filters = decryptFilters(response.data.filter.filters, userId);
    }

    return response.data;
  } catch (error) {
    console.error('Error saving filter:', error);
    throw error;
  }
};

/**
 * Save search query without filters
 * @param {string} name
 * @param {string} searchQuery
 * @param {boolean} isEvidence
 */
export const saveSearchQuery = async (name, searchQuery, isEvidence = false) => {
  return saveFilter(name, [], isEvidence, searchQuery, 'search');
};

/**
 * Save column selection
 * @param {string} name
 * @param {Object} columnVisibilityModel
 * @param {Array} columnOrder
 * @param {Object} searchContext
 */
export const saveColumnSelection = async (name, columnVisibilityModel, columnOrder, searchContext) => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const payload = {
      name,
      filters: [],
      isEvidence: searchContext?.isEvidence || false,
      searchQuery: null,
      type: 'columns',
      columnVisibilityModel,
      columnOrder,
      searchContext,
    };

    const response = await apiClient.post('/filters', payload);
    return response.data;
  } catch (error) {
    console.error('Error saving column selection:', error);
    throw error;
  }
};

/**
 * Save complete search (filters + columns + order)
 * @param {string} name
 * @param {Array} columnFilters
 * @param {string} searchQuery
 * @param {Object} columnVisibilityModel
 * @param {Array} columnOrder
 * @param {Object} searchContext
 */
export const saveCompleteSearch = async (
  name,
  columnFilters,
  searchQuery,
  columnVisibilityModel,
  columnOrder,
  searchContext,
) => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const encryptedFilters = columnFilters ? encryptFilters(columnFilters, userId) : [];

    const payload = {
      name,
      filters: encryptedFilters,
      isEvidence: searchContext?.isEvidence || false,
      searchQuery,
      type: 'complete',
      columnVisibilityModel,
      columnOrder,
      searchContext,
    };

    const response = await apiClient.post('/filters', payload);

    if (response.data.filter && response.data.filter.filters) {
      response.data.filter.filters = decryptFilters(response.data.filter.filters, userId);
    }

    return response.data;
  } catch (error) {
    console.error('Error saving complete search:', error);
    throw error;
  }
};

/**
 * Save source score weights
 * @param {string} name
 * @param {Array} sourceWeights - Array of {name: string, score: number}
 * @param {Object} searchContext
 */
export const saveScore = async (name, sourceWeights, searchContext) => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const encryptedWeights = encryptFilters(sourceWeights, userId);

    const payload = {
      name,
      filters: [], 
      isEvidence: false,
      searchQuery: null,
      type: 'score',
      sourceWeights: encryptedWeights, 
      searchContext,
    };

    const response = await apiClient.post('/filters', payload);

    if (response.data.filter && response.data.filter.sourceWeights) {
      response.data.filter.sourceWeights = decryptFilters(response.data.filter.sourceWeights, userId);
    }

    return response.data;
  } catch (error) {
    console.error('Error saving score:', error);
    throw error;
  }
};

// Get filters
export const getFilters = async () => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const response = await apiClient.get('/filters');

    if (response.data.filters && Array.isArray(response.data.filters)) {
      response.data.filters = response.data.filters.map(filter => {
        const decryptedFilter = {
          ...filter,
          filters: decryptFilters(filter.filters, userId),
          searchQuery: filter.searchQuery,
        };

        if (filter.type === 'score' && filter.sourceWeights) {
          decryptedFilter.sourceWeights = decryptFilters(filter.sourceWeights, userId);
        }

        return decryptedFilter;
      });
    }

    return response.data;
  } catch (error) {
    console.error('Error getting filters:', error);
    throw error;
  }
};

/**
 * Get filter by ID
 * @param {string} filterId
 */
export const getFilterById = async filterId => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const response = await getFilters();
    
    if (response.filters && Array.isArray(response.filters)) {
      const filter = response.filters.find(f => f.filterId === filterId);
      return filter || null;
    }

    return null;
  } catch (error) {
    console.error('Error getting filter by ID:', error);
    throw error;
  }
};

/**
 * Remove filter
 * @param {string} filterId
 */
export const deleteFilter = async filterId => {
  try {
    const response = await apiClient.delete(`/filters/${filterId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting filter:', error);
    throw error;
  }
};

/**
 * Update filter
 * @param {string} filterId
 * @param {Object} updates
 */
export const updateFilter = async (filterId, updates) => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const payload = { ...updates };

    if (updates.filters) {
      payload.filters = encryptFilters(updates.filters, userId);
    }

    const response = await apiClient.put(`/filters/${filterId}`, payload);

    if (response.data.filter && response.data.filter.filters) {
      response.data.filter.filters = decryptFilters(response.data.filter.filters, userId);
    }

    return response.data;
  } catch (error) {
    console.error('Error updating filter:', error);
    throw error;
  }
};

// Filters from LS to cloud
export const migrateLocalFiltersToCloud = async () => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const localFilters = localStorage.getItem('filters');
    if (!localFilters) return { migrated: 0 };

    const filters = JSON.parse(localFilters);
    const migrationPromises = filters.map((filterGroup, index) =>
      saveFilter(
        `Migrated Filter ${index + 1}`,
        Array.isArray(filterGroup) ? filterGroup : [filterGroup],
        filterGroup.isEvidence || false,
        null,
      ),
    );

    await Promise.all(migrationPromises);

    localStorage.removeItem('filters');

    return { migrated: filters.length };
  } catch (error) {
    console.error('Error migrating filters:', error);
    throw error;
  }
};
